'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import PhoneInput from 'react-phone-input-2'
import { sendEventBrevoWithoutAuthAction } from '@/actions/send-event-brevo'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  type AgendorFunnelConfig,
  type GraduateAnswer,
  GRADUATE_ANSWER_OPTIONS,
  GRADUATE_ANSWER_VALUES,
  formatGraduateDealDescription,
  getGraduateAnswerLabel,
  resolvePostgraduateAgendorFunnel,
} from '@/helpers/postgraduate-agendor-funnel'
import { useUnity } from '@/hooks/use-unity'
import { createDealPeople } from '@/http/create-deal-people-agendor'
import { createPeople } from '@/http/create-people-agendor'
import 'react-phone-input-2/lib/style.css'
import Swal from 'sweetalert2'
import { z } from 'zod'

const formSchema = z.object({
  fullName: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Formato de email inválido'),
  phone: z
    .string()
    .nonempty('Telefone é obrigatório')
    .transform(telefone => telefone.replace(/\D/g, '')),
  isGraduate: z.enum(GRADUATE_ANSWER_VALUES),
})

interface ModalBankSlipProps {
  children: React.ReactNode
  courseName: string | undefined
  courseId: string | undefined
  courseAmount: string | number | undefined | null
  title: string
  contactOrigin: string
}

type FormSchemaType = z.infer<typeof formSchema>

const SUCCESS_TEXT =
  'Obrigado pelo interesse em nossa pós-graduação. Sua solicitação foi recebida e nossa equipe entrará em contato em breve para dar continuidade ao atendimento.'

const SUBMISSION_ERROR_TEXT =
  'Não foi possível registrar sua solicitação. Verifique os dados e tente novamente.'

async function sendPostgraduateBrevoEvents({
  email,
  fullName,
  phone,
  courseId,
  courseName,
}: {
  email: string
  fullName: string
  phone: string
  courseId: string | undefined
  courseName: string | undefined
}) {
  await sendEventBrevoWithoutAuthAction({
    event: 'interest_pos',
    email,
    eventdata: {
      data: {
        course_id: courseId,
        course_name: courseName,
        email,
        nome: fullName,
        whatsapp: phone ?? '',
      },
    },
    properties: {
      course_name: courseName,
      nome: fullName,
      whatsapp: phone ?? '',
    },
  })
}

async function createPostgraduateAgendorDeal({
  fullName,
  email,
  phone,
  courseName,
  courseAmount,
  contactOrigin,
  graduateAnswer,
  brandName,
  agendorFunnel,
}: {
  fullName: string
  email: string
  phone: string
  courseName: string | undefined
  courseAmount: string | number | undefined | null
  contactOrigin: string
  graduateAnswer: GraduateAnswer
  brandName?: string | null
  agendorFunnel?: AgendorFunnelConfig | null
}) {
  const { data: person } = await createPeople({
    name: fullName,
    contact: {
      email,
      whatsapp: phone,
    },
  })

  const personId = person.id
  const funnel = resolvePostgraduateAgendorFunnel({
    brandName,
    agendorFunnel,
  })

  await createDealPeople({
    personId,
    dealAgendor: {
      title: `${personId} - ${fullName}`,
      products: [courseName ?? ''],
      funnel,
      description: formatGraduateDealDescription(courseName, graduateAnswer),
      value: Number(courseAmount),
      customFields: {
        origem_contato: contactOrigin,
        pos_graduacao_desejada: courseName ?? '',
        'voce_ja_e_graduado?': getGraduateAnswerLabel(graduateAnswer),
      },
    },
  })
}

export default function ModalBankSlip({
  children,
  courseName,
  courseId,
  courseAmount,
  title,
  contactOrigin,
}: ModalBankSlipProps) {
  const [open, setOpen] = useState(false)
  const { unity, metadataDetails } = useUnity()

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      isGraduate: 'sim',
    },
  })

  async function onSubmit(data: FormSchemaType) {
    try {
      const { fullName, phone, email, isGraduate } = data

      await sendPostgraduateBrevoEvents({
        email,
        fullName,
        phone,
        courseId,
        courseName,
      })

      await createPostgraduateAgendorDeal({
        fullName,
        email,
        phone,
        courseName,
        courseAmount,
        contactOrigin,
        graduateAnswer: isGraduate,
        brandName: unity.full_name,
        agendorFunnel: metadataDetails?.agendorFunnel,
      })

      setOpen(false)
      form.reset()

      Swal.fire({
        title: 'Formulário enviado com sucesso!',
        text: SUCCESS_TEXT,
        icon: 'success',
        confirmButtonText: 'Continuar',
      })
    } catch (_error) {
      Swal.fire({
        title: 'Erro ao enviar formulário',
        text: SUBMISSION_ERROR_TEXT,
        icon: 'error',
        confirmButtonText: 'Tentar novamente',
      })
    }
  }

  return (
    <Dialog defaultOpen={open} onOpenChange={() => setOpen(!open)} open={open}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-3xl">{title}</DialogTitle>
        </DialogHeader>
        <div className="mt-8 w-ful">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="mb-3 w-full">
                <FormLabel>Nome:</FormLabel>
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="h-12 rounded-lg"
                          placeholder="Nome Completo"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mb-3 w-full">
                <FormLabel>Email:</FormLabel>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          className="h-12 rounded-lg"
                          placeholder="Email"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mb-3 w-full">
                <FormLabel>Telefone:</FormLabel>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="mb-3">
                      <FormControl className="flex">
                        <PhoneInput
                          country={'br'}
                          inputStyle={{ height: '48px', width: '100%' }}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mb-3 w-full">
                <FormLabel>Voçê já é graduado?:</FormLabel>
                <FormField
                  control={form.control}
                  name="isGraduate"
                  render={({ field }) => (
                    <FormItem>
                      <Select defaultValue="sim" onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {GRADUATE_ANSWER_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
              <Button
                className="mt-4 rounded-full bg-[#E8AA17] font-semibold text-base text-black lg:h-12"
                type="submit"
              >
                Solicitar contato
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
